/**
 * Cache Analytics Utility
 * Tracks cache performance and provides insights
 */

import { ref, get, set, serverTimestamp } from 'firebase/database';
import { rtdb, CACHE_PATHS, logCache } from '../rtdbConfig';
import rtdbCache from '../rtdbCache';

class CacheAnalytics {
  constructor() {
    this.sessionStats = {
      hits: 0,
      misses: 0,
      writes: 0,
      errors: 0,
      sessionStart: Date.now()
    };
  }

  /**
   * Get comprehensive cache statistics
   */
  async getStats() {
    try {
      // Get global stats from RTDB
      const statsRef = ref(rtdb, `${CACHE_PATHS.METADATA}/stats`);
      const snapshot = await get(statsRef);
      const globalStats = snapshot.exists() ? snapshot.val() : {};

      // Get cache info
      const cacheInfo = rtdbCache.getCacheInfo();

      // Calculate hit rate
      const totalRequests = (globalStats.hits || 0) + (globalStats.misses || 0);
      const hitRate = totalRequests > 0 
        ? ((globalStats.hits || 0) / totalRequests * 100).toFixed(2) 
        : 0;

      // Calculate session duration
      const sessionDuration = Date.now() - this.sessionStats.sessionStart;
      const sessionMinutes = Math.floor(sessionDuration / 60000);

      return {
        global: {
          hits: globalStats.hits || 0,
          misses: globalStats.misses || 0,
          writes: globalStats.writes || 0,
          errors: globalStats.errors || 0,
          hitRate: `${hitRate}%`,
          totalRequests
        },
        session: {
          ...this.sessionStats,
          duration: `${sessionMinutes} minutes`
        },
        cache: {
          ...cacheInfo,
          estimatedSavings: this.calculateSavings(globalStats.hits || 0)
        }
      };
    } catch (error) {
      logCache('error', 'Failed to get analytics', { error: error.message });
      return null;
    }
  }

  /**
   * Calculate cost savings from cache hits
   */
  calculateSavings(hits) {
    // Rough estimates
    const avgApiCostPerCall = 0.002; // $0.002 per API call
    const avgTimesSavedMs = 400; // 400ms saved per cache hit
    
    return {
      apiCalls: hits,
      costSaved: `$${(hits * avgApiCostPerCall).toFixed(2)}`,
      timeSaved: `${(hits * avgTimesSavedMs / 1000).toFixed(0)} seconds`
    };
  }

  /**
   * Get most accessed cache entries
   */
  async getTopCacheEntries(limit = 10) {
    try {
      const cacheRef = ref(rtdb, 'cache');
      const snapshot = await get(cacheRef);
      
      if (!snapshot.exists()) return [];

      const entries = [];
      const processNode = (node, path = '') => {
        if (node && typeof node === 'object') {
          if (node.hits !== undefined && node.data !== undefined) {
            entries.push({
              path,
              hits: node.hits,
              lastUpdated: node.lastUpdated,
              ttl: node.ttl
            });
          } else {
            Object.keys(node).forEach(key => {
              processNode(node[key], path ? `${path}/${key}` : key);
            });
          }
        }
      };

      processNode(snapshot.val());
      
      // Sort by hits and return top entries
      return entries
        .sort((a, b) => b.hits - a.hits)
        .slice(0, limit);
    } catch (error) {
      logCache('error', 'Failed to get top entries', { error: error.message });
      return [];
    }
  }

  /**
   * Track cache performance over time
   */
  async recordPerformanceMetric(metric, value) {
    try {
      const metricsRef = ref(rtdb, `${CACHE_PATHS.METADATA}/performance/${Date.now()}`);
      await set(metricsRef, {
        metric,
        value,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      // Silent fail for analytics
    }
  }

  /**
   * Get cache size estimate
   */
  async getCacheSize() {
    try {
      const cacheRef = ref(rtdb, 'cache');
      const snapshot = await get(cacheRef);
      
      if (!snapshot.exists()) return { size: 0, formatted: '0 KB' };

      // Rough estimate of JSON size
      const jsonStr = JSON.stringify(snapshot.val());
      const sizeBytes = new Blob([jsonStr]).size;
      
      let formatted;
      if (sizeBytes < 1024) {
        formatted = `${sizeBytes} B`;
      } else if (sizeBytes < 1024 * 1024) {
        formatted = `${(sizeBytes / 1024).toFixed(2)} KB`;
      } else {
        formatted = `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`;
      }

      return {
        size: sizeBytes,
        formatted
      };
    } catch (error) {
      logCache('error', 'Failed to get cache size', { error: error.message });
      return { size: 0, formatted: '0 KB' };
    }
  }

  /**
   * Generate cache report
   */
  async generateReport() {
    const [stats, topEntries, cacheSize] = await Promise.all([
      this.getStats(),
      this.getTopCacheEntries(),
      this.getCacheSize()
    ]);

    const report = {
      timestamp: new Date().toISOString(),
      stats,
      topEntries,
      cacheSize,
      recommendations: this.getRecommendations(stats, topEntries)
    };

    logCache('info', 'Cache Analytics Report', report);
    return report;
  }

  /**
   * Get optimization recommendations
   */
  getRecommendations(stats, topEntries) {
    const recommendations = [];

    if (stats?.global?.hitRate) {
      const hitRate = parseFloat(stats.global.hitRate);
      if (hitRate < 50) {
        recommendations.push('Low hit rate - consider warming more popular data');
      } else if (hitRate > 90) {
        recommendations.push('Excellent hit rate - cache is working efficiently');
      }
    }

    if (topEntries?.length > 0) {
      const mostAccessed = topEntries[0];
      if (mostAccessed.hits > 100) {
        recommendations.push(`Consider longer TTL for ${mostAccessed.path}`);
      }
    }

    if (stats?.cache?.memoryCacheSize > 100) {
      recommendations.push('Large memory cache - consider clearing old entries');
    }

    return recommendations;
  }

  /**
   * Reset statistics
   */
  async resetStats() {
    try {
      const statsRef = ref(rtdb, `${CACHE_PATHS.METADATA}/stats`);
      await set(statsRef, {
        hits: 0,
        misses: 0,
        writes: 0,
        errors: 0,
        resetAt: serverTimestamp()
      });
      
      this.sessionStats = {
        hits: 0,
        misses: 0,
        writes: 0,
        errors: 0,
        sessionStart: Date.now()
      };
      
      logCache('info', 'Statistics reset');
    } catch (error) {
      logCache('error', 'Failed to reset stats', { error: error.message });
    }
  }
}

// Create singleton instance
const cacheAnalytics = new CacheAnalytics();

// Export for browser console debugging
if (typeof window !== 'undefined') {
  window.RTDBAnalytics = {
    stats: () => cacheAnalytics.getStats(),
    report: () => cacheAnalytics.generateReport(),
    topEntries: (limit) => cacheAnalytics.getTopCacheEntries(limit),
    size: () => cacheAnalytics.getCacheSize(),
    reset: () => cacheAnalytics.resetStats()
  };
}

export default cacheAnalytics;